import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { groupBy } from 'lodash'
import { alignRowsByZoneGroupId } from './alignZoneColumns'

const LOG_FIX = process.env.VUE_APP_DEBUG_FIX === '1' || process.env.NODE_ENV === 'development';

interface TransportCostsReportState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;


    filter: {
        // dZoneGroups: any;
        targetDates: Date[];
    };

    items: any[];
}

const state: TransportCostsReportState = {
    loading: false,
    loaded: true,
    fatalError: false,

    filter: {
        // dZoneGroups: null,
        targetDates: [new Date(), new Date()],
    },

    items: [],
};

function buildFilter(params: any) {
    const queryParams: any = {}

    if (params.targetDates) {
        queryParams.date_from = moment(params.targetDates[0]).format('YYYY-MM-DD');
        queryParams.date_to = moment(params.targetDates[1]).format('YYYY-MM-DD')
    }

    if (params.by) {
        queryParams.by = params.by;
    }

    if (params.only_movement) {
        queryParams.only_movement = params.only_movement;
    }

    return queryParams;
}

export const REPORT_DATA = 'REPORT_DATA';
export const TRANSPORT_COSTS = 'TRANSPORT_COSTS';

export const actions: ActionTree<TransportCostsReportState, RootState> = {

    loadData({ commit, state, dispatch }, params) {
        const vm = (this as any)._vm;
        commit('START_LOADING');

        return new Promise((resolve, reject) => {
          if (Object.values(params).length > 0) {
            vm.$db([
              vm.$q(REPORT_DATA)
                .raw('reports.transport_costs')
                .action(buildFilter(params)),
              vm.$q(TRANSPORT_COSTS)
                .raw('documents.list')
                .action({
                  "columns": {
                    "delivery_zone_group_id": "tcost.delivery_zone_group_id",
                    "date": "tcost.date",
                    "costs": {$sum: "tcost.costs"},
                },
                  "from": "s:transport_costs",
                  "from_as": "tcost",
                  "joins": [
                  ],
                  page_size: 10000,
                  "where": {
                      "$and": [
                        { "$gte": ["tcost.date", {"$": moment(params.targetDates[0]).format('YYYY-MM-DD')}] },
                        { "$lte": ["tcost.date", {"$": moment(params.targetDates[1]).format('YYYY-MM-DD')}] }
                      ]
                  },
                  groups: [
                    'tcost.delivery_zone_group_id',
                    'tcost.date'
                  ]
              }),
            ]).then(((result: ServerResult) => {
              commit('SET_REPORT_DATA', {
                data: result.getOperation(REPORT_DATA).getContent(),
                costs: result.getOperation(TRANSPORT_COSTS).getContent()
              });
              commit('LOADED_SUCCESS');
              
              resolve(null);
            }));
          } else {
            commit('LOADED_ERROR');
            resolve(null);
          }
        })
    },
};

export const mutations: MutationTree<TransportCostsReportState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.fatalError = true;
    },

    SET_REPORT_DATA(state, {data, costs}) {
      const documents: any = {};
      const sums: any = {}
      const group_costs = costs.documents.reduce((prev: any, item: any) => {
        prev[item.delivery_zone_group_id + '-' + item.date] = item.costs;
        return prev;
      }, {})

      // @ts-ignore
      const groups = Object.values(groupBy(data.transport_costs.documents, (item: any) => {
        return item.id + '-' + item.delivery_date;
      })).reduce((acc: any[], item: any) => {
        const arr = {
          client_cost: item.reduce((a: number, i: any) => {a += parseFloat(i.client_cost); return a}, 0),
          by_kg: 0,
          color: item[0].color,
          delivery_date: item[0].delivery_date,
          di_ids: item.reduce((a: any[], i: any) => {
            if (i.di_ids) {
              i.di_ids.map((id: any)=> {
                a.push(id)
              })
            } 
            return a}, []),
          id: item[0].id,
          name: item[0].name,
          no_money: item[0].no_money,
          total_weight: item.reduce((a: number, i: any) => {a += parseFloat(i.total_weight); return a}, 0),
          weight_deal: item.reduce((a: number, i: any) => {a += parseFloat(i.weight_deal); return a}, 0),
          weight_movement: item.reduce((a: number, i: any) => {a += parseFloat(i.weight_movement); return a}, 0),
          weight_return: item.reduce((a: number, i: any) => {a += parseFloat(i.weight_return); return a}, 0),
        }
        arr.by_kg = arr.total_weight > 0 ? arr.client_cost / arr.total_weight : 0
        acc.push(arr)
        return acc
      }, [])

      groups.map((item: any) => {
        if (!documents[item.delivery_date]) {
          documents[item.delivery_date] = []
        }
        const ids = item.di_ids ? item.di_ids.reduce((accumulator: any, value: any) => accumulator.concat(value), []) : []
        const no_money_any = ids.filter((di: any) => di.cost == 0).length != ids.length
        let class_name = '';
        if (item.id == '1') {
          class_name = item.no_money ? 'has-background-danger' : (no_money_any ? 'has-background-warning' : '')
        }
        documents[item.delivery_date].push({
          id: item.id,
          zone_id: item.id,
          by_kg: item.by_kg,
          delivery_date: item.delivery_date,
          name: item.name,
          total_weight: item.total_weight,
          client_cost: item.client_cost,
          color: item.color,
          no_money: item.no_money,
          di_ids: ids,
          weight_deal: item.weight_deal,
          weight_return: item.weight_return,
          weight_movement: item.weight_movement,
          class_name: class_name,
          costs: group_costs['d:delivery_zone_groups:' + item.id + '-' + item.delivery_date]
        })
        if (!sums[item.id]) {
          sums[item.id] = 0
        }
        sums[item.id] += parseFloat(item.client_cost)
      })

      const unsortedRows = Object.keys(documents).sort().map((date) => {
        return documents[date].filter((row: any) => {
          return sums[row.zone_id] > 0 || row.zone_id == 18 || true;
        })
      })

      if (LOG_FIX) {
        const beforeOrders = unsortedRows.map((row: any[]) =>
          row.map((cell: any) => String(cell.zone_id))
        )
        console.log('[FIX] TransportCostsReport zone column order before align', {
          dates: Object.keys(documents).sort(),
          beforeOrders,
        })
      }

      try {
        state.items = alignRowsByZoneGroupId(unsortedRows)

        if (LOG_FIX) {
          const afterOrders = state.items.map((row: any[]) =>
            row.map((cell: any) => String(cell.zone_id))
          )
          const aligned =
            afterOrders.length === 0 ||
            afterOrders.every(
              (order: string[]) =>
                order.length === afterOrders[0].length &&
                order.every((zoneId: string, idx: number) => zoneId === afterOrders[0][idx])
            )
          console.log('[FIX] TransportCostsReport zone column order after align', {
            afterOrders,
            aligned,
          })
          if (!aligned) {
            console.warn('[FIX] TransportCostsReport rows still misaligned by zone_id', {
              afterOrders,
            })
          }
        }
      } catch (error) {
        console.error('[FIX] TransportCostsReport failed to align zone columns', {
          error: error && (error as Error).message,
          stack: error && (error as Error).stack,
        })
        throw error
      }
    },
};

const getters = {
  totalData(state: TransportCostsReportState) {
    const header: any = {}
    const total: any = {
      total_weight: 0,
      client_cost: 0,
      weight_deal: 0,
      weight_return: 0,
      weight_movement: 0,
    }
    state.items.map((item: any) => {
      item.map((item: any, idx: number) => {
        if (!header[idx]) {
          header[idx] = {
            total_weight: 0,
            client_cost: 0,
            by_kg: 0,
          }
        }
        header[idx].total_weight += parseFloat(item.total_weight)
        header[idx].client_cost += parseFloat(item.client_cost)
        header[idx].by_kg = header[idx].total_weight > 0 ? header[idx].client_cost / header[idx].total_weight : 0
        total.total_weight += parseFloat(item.total_weight)
        total.client_cost += parseFloat(item.client_cost)
        total.weight_deal += parseFloat(item.weight_deal)
        total.costs_deal =  total.weight_deal * total.client_cost / total.total_weight 
        total.weight_return += parseFloat(item.weight_return)
        total.costs_return =  total.weight_return * total.client_cost / total.total_weight 
        total.weight_movement += parseFloat(item.weight_movement)
        total.costs_movement =  total.weight_movement * total.client_cost / total.total_weight 
      })
    })
    return {
      total,
      header: Object.values(header)
    }
  },
  exportData(state: TransportCostsReportState) {
    const toExportNumber = (value: any): number => {
      const n = typeof value === 'number' ? value : parseFloat(value)
      return Number.isFinite(n) ? n : 0
    }

    const csvContent: any[] = [];
    const header = ['Дата',]

    state.items.map((item: any) => {
        const row: any[] = [];
        row.push(item[0].delivery_date)
        item.map((item: any) => {
          if (!header.includes(item.name + ', масса, кг')) {
            header.push(item.name + ', масса, кг')
            header.push(item.name + ', затраты, руб')
            header.push(item.name + ', на кг, руб')
          }
          row.push(toExportNumber(item.total_weight))
          row.push(toExportNumber(item.client_cost))
          row.push(toExportNumber(item.by_kg))
        })
        csvContent.push(row)
    })
    return [
        header,
        ...csvContent
    ]
  }
};

export const STORE_KEY = '$_transport_costs_report';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
